import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { listProducts, productsByIds } from "../api/endpoints";
import ProductPhoto from "../components/ProductPhoto";
import { HandBlot, HandEllipse, HandSeal } from "../components/Hand";
import ProductScroller from "../components/ProductScroller";
import Sheet from "../components/Sheet";
import { CatalogSkeleton } from "../components/Skeletons";
import { getRecentlyViewedIds } from "../lib/recentlyViewed";
import { haptic } from "../lib/tg";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useUserStore } from "../store/user";
import { useLikesStore } from "../store/likes";
import type { Category, Product } from "../types";
import { CATEGORY_LABELS, categoryLabel } from "../types";

// Размер страницы для бесконечной прокрутки.
const PAGE_SIZE = 60;

// Геометрия сетки для виртуализации (CARD_BODY_H = .card__body height в styles.css).
const GRID_GAP = 12;
const CARD_BODY_H = 104;
const BUFFER_ROWS = 4;

// Пороги «популярного» товара (соцдоказательство).
const POPULAR_LIKES = 3;
const POPULAR_VIEWS = 25;

// Сколько дней товар считается новинкой с момента добавления (синхронно с backend).
const NEW_ARRIVAL_DAYS = 7;
const isNewArrival = (createdAt: string): boolean =>
  Date.now() - new Date(createdAt).getTime() < NEW_ARRIVAL_DAYS * 24 * 60 * 60 * 1000;

// Кеш живёт пока открыт каталог — переключение между категориями мгновенное.
// Храним загруженные товары + сколько уже загружено + есть ли ещё.
type CacheEntry = { items: Product[]; nextOffset: number; hasMore: boolean };
const _cache = new Map<string, CacheEntry>();

// Гендерные слова, которые убираем при извлечении размера из части строки.
const isGenderWord = (token: string): boolean =>
  /^(men'?s?|women'?s?|man|woman)$/i.test(token);

// Разбивает строку размера на токены, исключая гендерные слова.
// "men M" → ["m"],  "women's XL" → ["xl"],  "XXL" → ["xxl"]
const sizeTokens = (s: string): string[] =>
  s.split(/[\s/,]+/)
    .map((t) => t.trim())
    .filter((t) => t && !isGenderWord(t))
    .map((t) => t.toLowerCase());

// Проверяет, подходит ли размер товара под предпочтение с учётом пола.
// Для генdered строк ("men M / women XL") анализирует нужную часть.
// Сравнение токенов — точное (без regex), исключает ложные совпадения XL→L, 2XL и т.д.
const sizeMatch = (productSize: string, pref: string, gender?: string | null): boolean => {
  const lowerPref = pref.toLowerCase();
  const parts = productSize.split("/").map((s) => s.trim());

  const hasMalePart   = parts.some((p) => /\bmen'?s?\b/i.test(p) && !/\bwomen/i.test(p));
  const hasFemalePart = parts.some((p) => /\bwomen/i.test(p));
  const isGendered    = hasMalePart || hasFemalePart;

  if (!isGendered) {
    // Унисекс — токены всей строки
    return sizeTokens(productSize).includes(lowerPref);
  }

  // Генdered — берём только ту часть, которая совпадает с полом пользователя
  for (const part of parts) {
    const isMale   = /\bmen'?s?\b/i.test(part) && !/\bwomen/i.test(part);
    const isFemale = /\bwomen/i.test(part);

    if (gender === "male"   && !isMale)   continue;
    if (gender === "female" && !isFemale) continue;
    // Если пол не задан — проверяем любую часть (поведение унисекс)

    if (sizeTokens(part).includes(lowerPref)) return true;
  }

  return false;
};

const ProductCard = memo(function ProductCard({
  product,
  isMatch,
  isLiked,
  isNew,
  onLike,
}: {
  product: Product;
  isMatch: boolean;
  isLiked: boolean;
  isNew: boolean;
  onLike: (id: number) => void;
}) {
  const likes = product.likes ?? 0;
  const views = product.views ?? 0;
  const isPopular = likes >= POPULAR_LIKES || views >= POPULAR_VIEWS;
  return (
    <div style={{ position: "relative" }}>
      {isNew ? (
        <span className="pill" style={{
          position: "absolute", top: 7, left: 7, zIndex: 2,
          background: "var(--accent)", color: "#fff",
          padding: "3px 8px 2px", pointerEvents: "none",
        }}>✨ NEW</span>
      ) : isPopular ? (
        <span className="pill" style={{
          position: "absolute", top: 7, left: 7, zIndex: 2,
          background: "rgba(0,0,0,0.6)", color: "#fff",
          padding: "3px 8px 2px", pointerEvents: "none",
        }}>🔥 Хит</span>
      ) : null}
      <Link to={`/product/${product.id}`} className="card">
        {/* Статус штампом поверх фото: печать говорит то же, что бейдж, но
            голосом языка. Появляется только на проданных и забронированных —
            у доступных товаров кадр остаётся чистым. */}
        {product.status !== "available" && (
          <HandSeal
            variant={product.status === "sold" ? 1 : 2}
            className={`card__seal card__seal--${product.status === "sold" ? "sold" : "hold"}`}
          />
        )}
        <ProductPhoto productId={product.id} alt={product.title ?? product.sku} variant="thumb" blur={product.blur} />
        <div className="card__body">
          <p className="card__title">{product.title ?? categoryLabel(product.category)}</p>
          <p className="micro card__size">
            {isMatch
              ? <span style={{ color: "var(--accent)", fontWeight: 600 }}>✓ {product.size}</span>
              : <span style={{ color: "var(--hint)" }}>{product.size}</span>
            }
          </p>
          {/* Нижняя строка: цена + соцдоказательство в один ряд (экономит высоту). */}
          <div style={{ marginTop: "auto", display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
            <span className="card__price">{Math.round(product.price).toLocaleString("ru-RU")} ₽</span>
            {(likes > 0 || views > 0) && (
              <span className="micro" style={{ color: "var(--hint)", whiteSpace: "nowrap", flexShrink: 0 }}>
                {likes > 0 && `❤️ ${likes}`}{likes > 0 && views > 0 ? "  " : ""}{views > 0 && `👁 ${views}`}
              </span>
            )}
          </div>
        </div>
      </Link>
      <button
        onClick={() => { haptic.tap(); onLike(product.id); }}
        style={{
          position: "absolute", top: 7, right: 7,
          background: "rgba(0,0,0,0.35)",
          border: "none",
          borderRadius: "50%",
          width: 32, height: 32,
          fontSize: 16,
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: isLiked ? "#e53935" : "rgba(255,255,255,0.85)",
          lineHeight: 1,
        }}
      >
        {isLiked ? "♥" : "♡"}
      </button>
    </div>
  );
});

// Тип вещи. Статуса («новые», «проданные») здесь нет намеренно: раньше обе оси
// лежали в одном ряду из 23 кнопок, и выбор «Проданные» выглядел как ещё одна
// категория одежды. Теперь состояние товара — отдельный переключатель выше.
const CATEGORIES: (Category | "all")[] = [
  "all",
  "tshirt", "polo", "longsleeve",
  "hoodie", "olimp", "fleece", "sweater", "vest", "jersey",
  "suit", "shirt",
  "jeans", "pants", "shorts", "sweatpants",
  "blazer", "jacket", "coat", "puffer",
  "shoes", "hat", "accessory",
];

type Status = "available" | "new" | "sold";
const STATUSES: { value: Status; label: string }[] = [
  { value: "available", label: "В наличии" },
  { value: "new",       label: "✨ Новинки" },
  { value: "sold",      label: "Проданные" },
];

type Sort = "new" | "price_asc" | "price_desc";
const SORTS: { value: Sort; label: string }[] = [
  { value: "new",        label: "Сначала новые" },
  { value: "price_asc",  label: "Дешевле" },
  { value: "price_desc", label: "Дороже" },
];

const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "One size"];

export default function CatalogPage() {
  const { user } = useUserStore();
  const { ids: likedIds, load: loadLikes, toggle: toggleLike } = useLikesStore();
  // Витрина ведёт сюда с предвыбранной категорией (/catalog?category=jeans,
  // ?category=new) или с включённым «моим размером» (?my=1). Значения читаем
  // один раз при монтировании: дальше состоянием владеет сам каталог.
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState<Category | "all">(() => {
    const c = searchParams.get("category");
    return (c && (CATEGORIES as string[]).includes(c) ? c : "all") as Category | "all";
  });
  const [status, setStatus] = useState<Status>(() => {
    const s = searchParams.get("status");
    return (s && STATUSES.some((x) => x.value === s) ? s : "available") as Status;
  });
  const [sort, setSort] = useState<Sort>("new");
  const [size, setSize] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [search, setSearch] = useState("");          // то, что вводит пользователь
  const [query, setQuery] = useState("");            // дебаунснутое значение для запроса
  const [mySize, setMySize] = useState(() => searchParams.get("my") === "1");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [items, setItems] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);   // принудительный рефетч (pull-to-refresh)
  const [pull, setPull] = useState(0);               // текущее смещение оттягивания, px
  const [refreshing, setRefreshing] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const pullStartRef = useRef<number | null>(null);
  const nextOffsetRef = useRef(0);                   // следующий offset для подгрузки
  const sentinelRef = useRef<HTMLDivElement>(null);  // маркер конца списка

  // Виртуализация каталога: рендерим только видимые ряды (оконный рендеринг).
  const gridWrapRef = useRef<HTMLDivElement | null>(null);
  const [colW, setColW] = useState(0);               // ширина колонки карточки, px
  const [cols, setCols] = useState(2);               // число колонок (адаптив: 2/3/4)
  const [scrollTop, setScrollTop] = useState(0);     // текущий window.scrollY

  // Число колонок по ширине сетки: телефон 2, планшет 3, десктоп 4.
  const colsFor = (w: number) => (w >= 1080 ? 4 : w >= 680 ? 3 : 2);
  // Пересчёт ширины колонки и их числа по фактической ширине грид-обёртки.
  const measure = (el: HTMLElement) => {
    const c = colsFor(el.clientWidth);
    setCols(c);
    setColW((el.clientWidth - GRID_GAP * (c - 1)) / c);
  };

  useDocumentTitle("Каталог");

  useEffect(() => { loadLikes(); }, [loadLikes]);

  // Виртуализация: следим за прокруткой окна (rAF-троттлинг) и шириной колонки.
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; setScrollTop(window.scrollY); });
    };
    const onResize = () => {
      const el = gridWrapRef.current;
      if (el) measure(el);
      onScroll();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // Недавно просмотренные — грузим один раз, выстраиваем в порядке просмотра.
  const [recent, setRecent] = useState<Product[]>([]);
  useEffect(() => {
    const ids = getRecentlyViewedIds();
    if (!ids.length) return;
    productsByIds(ids)
      .then((items) => {
        const byId = new Map(items.map((p) => [p.id, p]));
        setRecent(ids.map((id) => byId.get(id)).filter((p): p is Product => !!p));
      })
      .catch(() => {});
  }, []);

  // Показываем блок только в дефолтном виде (без поиска/фильтров/категории).
  const showRecent =
    status === "available" && category === "all" && !query && !size && !priceMin && !priceMax && recent.length > 1;

  // Дебаунс поиска — не дёргаем бэкенд на каждое нажатие.
  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Seed перемешки каталога: один на сессию, обновляется при pull-to-refresh.
  // Применяется при обычном просмотре (не «Новые»/«Проданные»/поиск) для случайного,
  // но стабильного между страницами порядка.
  const [seed, setSeed] = useState(() => Math.random().toString(36).slice(2, 10));
  const hasSizePref = !!(user?.clothing_size || user?.shoe_size);

  // Перемешка уместна только в дефолтном порядке: при явной сортировке по цене
  // и в срезах «новинки»/«проданные» она спорит с ожидаемым порядком.
  const useShuffle = status === "available" && sort === "new" && !query;

  // Текущий ключ кэша (нужен и для рефреша).
  const cacheKey = `${status}|${category}|${sort}|${mySize}|${size}|${priceMin}|${priceMax}|${query}|${useShuffle ? seed : ""}`;

  // Pull-to-refresh: новый seed (перетасовать), сбрасываем кэш среза и перезапрашиваем.
  const doRefresh = () => {
    _cache.delete(cacheKey);
    setSeed(Math.random().toString(36).slice(2, 10));
    setRefreshing(true);
    setReloadTick((t) => t + 1);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    pullStartRef.current = window.scrollY <= 0 ? e.touches[0].clientY : null;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (pullStartRef.current == null) return;
    const dy = e.touches[0].clientY - pullStartRef.current;
    if (dy > 0) setPull(Math.min(dy * 0.5, 80)); // с сопротивлением, максимум 80px
  };
  const onTouchEnd = () => {
    if (pull > 60) { haptic.tap(); doRefresh(); }
    setPull(0);
    pullStartRef.current = null;
  };

  // Параметры запроса для текущего среза + заданного offset.
  const buildParams = (offset: number) => ({
    category: category === "all" ? undefined : category,
    size: size || undefined,
    price_min: priceMin ? Number(priceMin) : undefined,
    price_max: priceMax ? Number(priceMax) : undefined,
    q: query || undefined,
    show_sold: status === "sold" ? true : undefined,
    only_available: status === "sold" ? false : undefined,
    new_only: status === "new" ? true : undefined,
    // «Мой размер» фильтрует на сервере. Раньше он отбирал на клиенте и ради
    // этого догружал ВЕСЬ каталог страницами — восемь сотен товаров на телефон
    // ради десятка совпадений.
    my_size: mySize && hasSizePref ? true : undefined,
    sort: sort === "new" ? undefined : sort,
    seed: useShuffle ? seed : undefined,
    limit: PAGE_SIZE,
    offset,
  });

  // Первая страница среза (при смене фильтров/категории/поиска или рефреше).
  useEffect(() => {
    const key = cacheKey;
    abortRef.current?.abort();

    // Новый срез — наверх (и виртуализация считает окно от начала списка).
    window.scrollTo({ top: 0 });
    setScrollTop(0);

    if (_cache.has(key)) {
      const entry = _cache.get(key)!;
      setItems(entry.items);
      nextOffsetRef.current = entry.nextOffset;
      setHasMore(entry.hasMore);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    listProducts(buildParams(0))
      .then((data) => {
        if (ctrl.signal.aborted) return;
        const more = data.length === PAGE_SIZE;
        nextOffsetRef.current = data.length;
        setItems(data);
        setHasMore(more);
        _cache.set(key, { items: data, nextOffset: data.length, hasMore: more });
      })
      .finally(() => {
        if (!ctrl.signal.aborted) { setLoading(false); setRefreshing(false); }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, category, sort, mySize, size, priceMin, priceMax, query, reloadTick, seed]);

  // Подгрузка следующей страницы (вызывается при достижении конца списка).
  const loadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    const key = cacheKey;
    const off = nextOffsetRef.current;
    setLoadingMore(true);
    listProducts(buildParams(off))
      .then((data) => {
        const more = data.length === PAGE_SIZE;
        nextOffsetRef.current = off + data.length;
        setHasMore(more);
        setItems((prev) => {
          const merged = [...prev, ...data];
          _cache.set(key, { items: merged, nextOffset: off + data.length, hasMore: more });
          return merged;
        });
      })
      .finally(() => setLoadingMore(false));
  };

  // IntersectionObserver на маркер конца списка → автоподгрузка.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { rootMargin: "600px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, loadingMore, cacheKey, mySize, items.length]);

  // Число активных фильтров (кроме категории) для бейджа на кнопке.
  const activeFilterCount = [size, priceMin, priceMax].filter(Boolean).length;

  // Предвычисляем Set ID товаров, подходящих под размер пользователя.
  // Пересчитывается только при смене items или параметров пользователя — не на каждый рендер.
  const matchedIds = useMemo(() => {
    if (!hasSizePref) return new Set<number>();
    return new Set(
      items
        .filter((p) => {
          const pref = p.category === "shoes" ? user?.shoe_size : user?.clothing_size;
          return pref ? sizeMatch(p.size, pref, user?.gender) : false;
        })
        .map((p) => p.id)
    );
  }, [items, user?.clothing_size, user?.shoe_size, user?.gender, hasSizePref]);

  // Сервер отбирает по размеру грубо: в строке «men M / women XL» он совпадает
  // с любой половиной. Пол знает только клиентский sizeMatch, поэтому уже
  // суженную выборку он дочищает — но каталог целиком ради этого больше не грузит.
  const displayItems = useMemo(
    () => (mySize && hasSizePref ? items.filter((p) => matchedIds.has(p.id)) : items),
    [mySize, hasSizePref, items, matchedIds]
  );

  // Оконный рендеринг: показываем только ряды вблизи вьюпорта.
  // Высота карточки детерминирована (фото 3/4 + тело CARD_BODY_H), поэтому ряд считаем по colW.
  const rowH = colW > 0 ? colW * (4 / 3) + CARD_BODY_H + GRID_GAP : 0;
  const totalRows = Math.ceil(displayItems.length / cols);
  let firstRow = 0;
  let lastRow = totalRows;
  if (rowH > 0 && gridWrapRef.current) {
    const gridTop = gridWrapRef.current.getBoundingClientRect().top + window.scrollY;
    const top = scrollTop - gridTop;
    firstRow = Math.max(0, Math.floor(top / rowH) - BUFFER_ROWS);
    lastRow = Math.min(totalRows, Math.ceil((top + window.innerHeight) / rowH) + BUFFER_ROWS);
    if (firstRow > lastRow) firstRow = lastRow;  // защита при резкой смене среза
  }
  const visibleItems = displayItems.slice(firstRow * cols, lastRow * cols);
  const padTop = firstRow * rowH;
  const padBottom = Math.max(0, (totalRows - lastRow) * rowH);

  // Условия для подписки из того, что человек сейчас искал: категория, размер
  // (явный фильтр или свой из профиля) и верхняя граница цены.
  const subscribeQuery = new URLSearchParams(
    Object.entries({
      category: category === "all" ? "" : category,
      size: size || (mySize ? (user?.clothing_size ?? "") : ""),
      price_max: priceMax,
    }).filter(([, v]) => v) as [string, string][],
  ).toString();

  const resetFilters = () => {
    setSize("");
    setPriceMin("");
    setPriceMax("");
  };

  return (
    <div className="container container--wide" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      {/* Индикатор pull-to-refresh */}
      {(pull > 0 || refreshing) && (
        <div className="micro" style={{
          height: refreshing ? 36 : pull,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--hint)", overflow: "hidden",
          transition: refreshing ? "height 0.2s" : undefined,
        }}>
          {refreshing ? "Обновляем…" : pull > 60 ? "↑ Отпустите для обновления" : "↓ Потяните для обновления"}
        </div>
      )}
      {/* Заголовок стоит на своей строке: рисованная обводка выходит за габарит
          слова, и кнопки рядом её перерезали. */}
      <h1 style={{ margin: "8px 0 0" }}>
        <span className="hand-wrap">
          Каталог
          <HandEllipse className="hand-ellipse" />
        </span>
      </h1>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", margin: "14px 0 8px", gap: 8 }}>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          {hasSizePref && (
            <button
              className={mySize ? "btn" : "btn btn--secondary"}
              style={{ width: "auto", padding: "8px 12px", fontSize: 13 }}
              onClick={() => { haptic.select(); setMySize((v) => !v); }}
            >
              {mySize ? "✓ Мой размер" : "Мой размер"}
            </button>
          )}
          <button
            className="btn btn--secondary"
            style={{ width: "auto", padding: "8px 14px", position: "relative" }}
            onClick={() => { haptic.tap(); setFiltersOpen(true); }}
          >
            Фильтры{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
        </div>
      </div>

      {/* Строка поиска по названию / артикулу. */}
      <div style={{ position: "relative", marginBottom: 10 }}>
        <span style={{
          position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
          color: "var(--hint)", fontSize: 15, pointerEvents: "none",
        }}>🔍</span>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по названию или артикулу"
          style={{
            width: "100%", padding: "10px 36px 10px 36px",
            borderRadius: 12, border: "1px solid var(--border)",
            background: "var(--secondary-bg)", color: "var(--text)",
            fontSize: 15, outline: "none", boxSizing: "border-box",
          }}
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            style={{
              position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", color: "var(--hint)",
              fontSize: 18, cursor: "pointer", lineHeight: 1, padding: 4,
            }}
            aria-label="Очистить"
          >×</button>
        )}
      </div>

      {/* Две оси, два ряда. Состояние товара — сегментированный переключатель на
          всю ширину: вариантов три, они взаимоисключающие и не должны уезжать в
          горизонтальный скролл вместе с двумя десятками категорий. */}
      <div className="segmented">
        {STATUSES.map((st) => (
          <button
            key={st.value}
            className={st.value === status ? "active" : ""}
            onClick={() => { haptic.select(); setStatus(st.value); }}
          >
            {st.label}
          </button>
        ))}
      </div>

      {/* Тип вещи. */}
      <div className="tabs">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            className={c === category ? "active" : ""}
            onClick={() => { haptic.select(); setCategory(c); }}
          >
            {c === "all" ? "Все" : CATEGORY_LABELS[c as Category]}
          </button>
        ))}
      </div>

      {/* Фильтры — нижней шторкой: список под ними не прыгает, а кнопки
          «сбросить/показать» всегда под большим пальцем. */}
      <Sheet open={filtersOpen} title="Фильтры" onClose={() => setFiltersOpen(false)}>
        <div className="field">
          <label>Сортировка</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {SORTS.map((o) => (
              <button
                key={o.value}
                className={`chip${o.value === sort ? " chip--on" : ""}`}
                onClick={() => { haptic.select(); setSort(o.value); }}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label>Размер</label>
          <select value={size} onChange={(e) => setSize(e.target.value)}>
            <option value="">Любой</option>
            {SIZES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className="row">
          <div className="field">
            <label>Цена от ₽</label>
            <input
              type="number"
              inputMode="numeric"
              placeholder="0"
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Цена до ₽</label>
            <input
              type="number"
              inputMode="numeric"
              placeholder="∞"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
            />
          </div>
        </div>

        {/* Счётчик в кнопке — обещание результата до закрытия шторки: фильтры
            применяются на лету, и «Показать» лишь убирает шторку с дороги. */}
        <button className="btn" onClick={() => { haptic.tap(); setFiltersOpen(false); }}>
          Показать{loading ? "" : ` (${displayItems.length}${hasMore ? "+" : ""})`}
        </button>
        {activeFilterCount > 0 && (
          <>
            <div style={{ height: 8 }} />
            <button className="btn btn--secondary" onClick={() => { haptic.tap(); resetFilters(); }}>
              Сбросить фильтры
            </button>
          </>
        )}
      </Sheet>

      {/* Недавно просмотренные — возврат к товарам внутри сессии. */}
      {showRecent && <ProductScroller title="Вы смотрели" products={recent} />}

      {loading && <CatalogSkeleton />}
      {!loading && displayItems.length === 0 && (
        <div className="empty">
          <HandBlot className="empty__blot" />
          <p style={{ color: "var(--hint)", margin: 0 }}>
            {mySize ? "Товаров вашего размера нет."
              : status === "new" ? "Пока нет новинок."
              : "Ничего не найдено."}
          </p>
          {/* Пустая выдача — лучший момент предложить подписку: человек уже
              сказал, что ищет. Условия переносим в форму, чтобы он не набирал
              их заново. Для «проданных» это бессмысленно — там и не должно быть
              наличия. */}
          {status !== "sold" && (
            <>
              <p style={{ color: "var(--hint)", margin: 0, fontSize: 13, maxWidth: 280 }}>
                Сообщим, когда такое приедет — бот напишет вам первым.
              </p>
              <Link to={`/subscriptions?${subscribeQuery}`} className="btn btn--secondary" style={{ width: "auto" }}>
                🔔 Сообщить о поступлении
              </Link>
            </>
          )}
        </div>
      )}

      {/* Сетка карточек товаров (виртуализированная: рендерим только видимые ряды,
          высоту невидимых компенсируют спейсеры — DOM не растёт при длинной прокрутке). */}
      {!loading && displayItems.length > 0 && (
        <div
          ref={(el) => {
            gridWrapRef.current = el;
            if (el && colW === 0) measure(el);
          }}
        >
          {padTop > 0 && <div style={{ height: padTop }} />}
          {/* minmax(0, 1fr), а не 1fr: иначе колонка не имеет права стать уже
              своего min-content, и строка размера с nowrap («women's 40 / men's s»)
              раздувает одну колонку за счёт другой — карточки в ряду выходят
              разного размера. То же и в `.grid` в styles.css. */}
          <div className="grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
            {visibleItems.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                isMatch={matchedIds.has(p.id)}
                isLiked={likedIds.has(p.id)}
                isNew={isNewArrival(p.created_at)}
                onLike={toggleLike}
              />
            ))}
          </div>
          {padBottom > 0 && <div style={{ height: padBottom }} />}
        </div>
      )}

      {/* Маркер конца списка + индикатор подгрузки (бесконечная прокрутка). */}
      {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
      {loadingMore && (
        <p style={{ textAlign: "center", color: "var(--hint)", padding: "12px 0" }}>Загрузка…</p>
      )}

      {/* Кнопка «наверх» — появляется после прокрутки. */}
      {scrollTop > 600 && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Наверх"
          style={{
            position: "fixed", right: 16, bottom: 84, zIndex: 50,
            width: 44, height: 44, borderRadius: "50%",
            background: "color-mix(in srgb, var(--bg) 55%, transparent)",
            backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
            color: "var(--text)", border: "1px solid var(--border)",
            fontSize: 20, cursor: "pointer",
            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >↑</button>
      )}
    </div>
  );
}
